import React from "react";
import { Autocomplete, Grid, TextField, Typography, Switch } from "@mui/material";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import { PendingBankAccountAction } from "../../../services/services";
import FormDialog from "../../../components/FormDialog";
import { INTERNATIONAL_COUNTRY_LIST } from "../../../utils/constants";
import { styles } from "../../../utils/styles";

// Bhutan is selected most often, so pin it to the top for faster selection
const BANK_COUNTRY_OPTIONS = [
  ...INTERNATIONAL_COUNTRY_LIST.filter((c) => c.name === "BHUTAN"),
  ...INTERNATIONAL_COUNTRY_LIST.filter((c) => c.name !== "BHUTAN"),
];

const CreateBankAccountDialog = ({ data = {}, onClose, cb }) => {
  const {
    id,
    account_number,
    bank,
    bank_address,
    bank_transfer_fee,
    beneficiary_address,
    beneficiary_bank_code,
    beneficiary_branch_code,
    beneficiary_name,
    bic_swift_code,
    iban,
    intermediary_bank,
    intermediary_bank_code,
    intermediary_name,
    settlement_fee,
    account_name,
    bank_country,
    bank_branch_code,
  } = data?.details || {};

  return (
    <Formik
      initialValues={{
        account_number: account_number || "",
        bank: bank || "",
        bank_address: bank_address || "",
        bic_swift_code: bic_swift_code || "",
        beneficiary_name: beneficiary_name || "",
        beneficiary_address: beneficiary_address || "",
        beneficiary_bank_code: beneficiary_bank_code || "",
        beneficiary_branch_code: beneficiary_branch_code || "",
        iban: iban || "",
        intermediary_bank: intermediary_bank || "",
        intermediary_bank_code: intermediary_bank_code || "",
        intermediary_name: intermediary_name || "",
        settlement_fee: settlement_fee ?? 0,
        bank_transfer_fee: bank_transfer_fee || "",
        account_name: account_name || "",
        bank_country: bank_country || "",
        bank_branch_code: bank_branch_code || "",
        transfer_charge_pay_by: data?.details?.transfer_charge_pay_by || "Oxygen7",
        is_active: data?.details?.hasOwnProperty('is_active') ? data.details.is_active : true,
      }}
      validationSchema={Yup.object().shape({
        account_number: Yup.string().required("Account Number is required"),
        bank: Yup.string().required("Bank Name is required"),
        account_name: Yup.string().required("Account Name is required"),
        bank_country: Yup.string().required("Bank Country is required"),
        bic_swift_code: Yup.string().required("BIC/SWIFT Code is required"),
        iban: Yup.string(),
        settlement_fee: Yup.number()
          .typeError("Must be a number")
          .min(0, "Must be greater than or equal to 0")
          .max(999999999999, "Must be smaller than 999,999,999,999")
          .required("Settlement Fee is required"),
      })}
      onSubmit={async (values, { setSubmitting }) => {
        const trimmedValues = { ...values };
        const fieldsToTrim = [
          "account_number", "bank", "bank_address", "bic_swift_code",
          "beneficiary_name", "beneficiary_address", "beneficiary_bank_code",
          "beneficiary_branch_code", "iban", "intermediary_bank",
          "intermediary_bank_code", "intermediary_name", "account_name",
          "bank_branch_code"
        ];
        fieldsToTrim.forEach(field => {
          if (trimmedValues[field]) trimmedValues[field] = trimmedValues[field].trim();
        });

        await PendingBankAccountAction(
          id ? "update" : "add",
          {
            ...trimmedValues,
            bank_transfer_fee: values.bank_transfer_fee ? values.bank_transfer_fee : 0,
            settlement_fee: values.settlement_fee ? Number(values.settlement_fee) : 0,
            is_active: values.is_active,
            pk: id || ""
          },
          onClose
        );
        setSubmitting(false);
      }}
    >
      {(props) => {
        const {
          values,
          touched,
          errors,
          isSubmitting,
          handleChange,
          handleBlur,
          setFieldValue,
          handleSubmit,
        } = props;
        return (
          <FormDialog
            open={true}
            onClose={onClose}
            title={
              id ? "Edit Bank Account" : "Add Bank Account"
            }
            scroll="paper"
            handleSubmit={handleSubmit}
            loading={isSubmitting}
          >
            <Form autoComplete="off" noValidate>
              <Grid item sm={12} xs={12} sx={{ py: 2, px: 2 }}>

                <Typography variant="h6" my={1}>
                  Bank Information
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={3}>
                    <Typography align="right">Account Number *</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Account Number"
                      margin="dense"
                      fullWidth
                      id="account_number"
                      name="account_number"
                      size="small"
                      color="secondary"
                      value={values.account_number}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(
                        touched.account_number && errors.account_number
                      )}
                      helperText={
                        touched.account_number && errors.account_number
                      }
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Typography align="right">Account Name *</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Account Name"
                      margin="dense"
                      fullWidth
                      id="account_name"
                      name="account_name"
                      size="small"
                      color="secondary"
                      value={values.account_name}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(
                        touched.account_name && errors.account_name
                      )}
                      helperText={
                        touched.account_name && errors.account_name
                      }
                      disabled={isSubmitting}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <Typography align="right">Bank Name *</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Bank Name"
                      margin="dense"
                      fullWidth
                      id="bank"
                      name="bank"
                      size="small"
                      color="secondary"
                      value={values.bank}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(touched.bank && errors.bank)}
                      helperText={touched.bank && errors.bank}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">Bank Address</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Bank Address"
                      margin="dense"
                      fullWidth
                      id="bank_address"
                      name="bank_address"
                      size="small"
                      color="secondary"
                      value={values.bank_address}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(
                        touched.bank_address && errors.bank_address
                      )}
                      helperText={touched.bank_address && errors.bank_address}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">Bank Country *</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Autocomplete
                      options={BANK_COUNTRY_OPTIONS}
                      getOptionLabel={(option) => option.name}
                      value={BANK_COUNTRY_OPTIONS.find(option => option.name === values.bank_country?.toUpperCase()) || null}
                      onChange={(event, newValue) => {
                        setFieldValue("bank_country", newValue ? newValue.name : "");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Bank Country"
                          size="small"
                          margin="dense"
                          error={Boolean(touched.bank_country && errors.bank_country)}
                          helperText={touched.bank_country && errors.bank_country}
                          disabled={isSubmitting}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">Bank Branch Code</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Bank Branch Code"
                      margin="dense"
                      fullWidth
                      id="bank_branch_code"
                      name="bank_branch_code"
                      size="small"
                      color="secondary"
                      value={values.bank_branch_code}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(
                        touched.bank_branch_code && errors.bank_branch_code
                      )}
                      helperText={touched.bank_branch_code && errors.bank_branch_code}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">
                      BIC/SWIFT Code *
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="BIC/SWIFT Code"
                      margin="dense"
                      fullWidth
                      id="bic_swift_code"
                      name="bic_swift_code"
                      size="small"
                      color="secondary"
                      value={values.bic_swift_code}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(
                        touched.bic_swift_code && errors.bic_swift_code
                      )}
                      helperText={
                        touched.bic_swift_code && errors.bic_swift_code
                      }
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">
                      IBAN
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="IBAN"
                      margin="dense"
                      fullWidth
                      id="iban"
                      name="iban"
                      size="small"
                      color="secondary"
                      value={values.iban}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(touched.iban && errors.iban)}
                      helperText={touched.iban && errors.iban}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">Bank Transfer Fee</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Bank Transfer Fee"
                      margin="dense"
                      fullWidth
                      id="bank_transfer_fee"
                      name="bank_transfer_fee"
                      size="small"
                      color="secondary"
                      type="number"
                      value={values.bank_transfer_fee}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(
                        touched.bank_transfer_fee && errors.bank_transfer_fee
                      )}
                      helperText={
                        touched.bank_transfer_fee && errors.bank_transfer_fee
                      }
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">Beneficiary Name</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Beneficiary Name"
                      margin="dense"
                      fullWidth
                      id="beneficiary_name"
                      name="beneficiary_name"
                      size="small"
                      color="secondary"
                      value={values.beneficiary_name}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">Beneficiary Address</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Beneficiary Address"
                      margin="dense"
                      fullWidth
                      id="beneficiary_address"
                      name="beneficiary_address"
                      size="small"
                      color="secondary"
                      value={values.beneficiary_address}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">Beneficiary Bank Code</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Beneficiary Bank Code"
                      margin="dense"
                      fullWidth
                      id="beneficiary_bank_code"
                      name="beneficiary_bank_code"
                      size="small"
                      color="secondary"
                      value={values.beneficiary_bank_code}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">
                      Beneficiary Branch Code
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Beneficiary Branch Code"
                      margin="dense"
                      fullWidth
                      id="beneficiary_branch_code"
                      name="beneficiary_branch_code"
                      size="small"
                      color="secondary"
                      value={values.beneficiary_branch_code}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">Intermediary Bank</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Intermediary Bank"
                      margin="dense"
                      fullWidth
                      id="intermediary_bank"
                      name="intermediary_bank"
                      size="small"
                      color="secondary"
                      value={values.intermediary_bank}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">
                      Intermediary Bank Code
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Intermediary Bank Code"
                      margin="dense"
                      fullWidth
                      id="intermediary_bank_code"
                      name="intermediary_bank_code"
                      size="small"
                      color="secondary"
                      value={values.intermediary_bank_code}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">Intermediary Name</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Intermediary Name"
                      margin="dense"
                      fullWidth
                      id="intermediary_name"
                      name="intermediary_name"
                      size="small"
                      color="secondary"
                      value={values.intermediary_name}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                    />
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">Transfer Charge Pay By</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      select
                      label="Transfer Charge Pay By"
                      margin="dense"
                      fullWidth
                      id="transfer_charge_pay_by"
                      name="transfer_charge_pay_by"
                      size="small"
                      color="secondary"
                      value={values.transfer_charge_pay_by}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      SelectProps={{ native: true }}
                      disabled={isSubmitting}
                    >
                      <option value="OxPay">Oxygen7</option>
                      <option value="Merchant">Merchant</option>
                      <option value="Share">Share</option>
                    </TextField>
                  </Grid>

                  <Grid item xs={3}>
                    <Typography align="right">Settlement Fee *</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Settlement Fee"
                      margin="dense"
                      fullWidth
                      id="settlement_fee"
                      name="settlement_fee"
                      size="small"
                      color="secondary"
                      type="number"
                      value={values.settlement_fee}
                      onChange={(e) => {
                        handleChange(e);
                      }}
                      onBlur={handleBlur}
                      error={Boolean(
                        touched.settlement_fee && errors.settlement_fee
                      )}
                      helperText={
                        touched.settlement_fee && errors.settlement_fee
                      }
                      disabled={isSubmitting}
                    />
                  </Grid>

                  {/* Empty space to push Active to the right side */}
                  <Grid item xs={6}></Grid>

                  <Grid item xs={3}>
                    <Typography align="right">Active</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Switch
                      checked={values.is_active}
                      onChange={(e) => setFieldValue("is_active", e.target.checked)}
                      name="is_active"
                      disabled={isSubmitting}
                      sx={{ 
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: styles.customButton.backgroundColor,
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          backgroundColor: styles.customButton.backgroundColor,
                        },
                      }}
                    />
                  </Grid>

                </Grid>
              </Grid>
            </Form>
          </FormDialog>
        );
      }}
    </Formik>
  );
};

export default CreateBankAccountDialog;
